import { faExclamationCircle } from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { BlockUI } from 'primereact/blockui'
import { Button } from 'primereact/button'
import { Dialog } from 'primereact/dialog'
import { InputText } from 'primereact/inputtext'
import { RadioButton } from 'primereact/radiobutton'
import { Tooltip } from 'primereact/tooltip'
import { classNames } from 'primereact/utils'
import Dropdown from '../../../../../shared/components/dropdown/Dropdown'
import styles from './GenerateDocumentModal.module.scss'
import { documentTypeOptions, UseGenerateDocumentModalReturnType } from './useGenerateDocument'
import React from 'react'

type Props = {
  hook: UseGenerateDocumentModalReturnType
}

const GenerateDocumentModal = (props: Props) => {
  const {
    visible,
    hide,
    documentsQuery,
    selectedTemplateId,
    setSelectedTemplateId,
    searchPhrase,
    setSearchPhrase,
    filteredDocuments,
    templateInfoQuery,
    selectedDocumentType,
    setSelectedDocumentType,
    isNoDocumentsFound,
    canGenerate,
    generateDocument,
    isGeneratingDocument,
    documentName,
    setDocumentName,
  } = props.hook

  const blockTemplate = (
    <>
      <i className="pi pi-spin pi-spinner" style={{ fontSize: '2rem' }}></i>
    </>
  )

  const renderDocumentList = () => {
    if (documentsQuery.isFetching) return null

    if (filteredDocuments?.length === 0) {
      return <div style={{ textAlign: 'center', margin: '2rem 0rem' }}>No results found</div>
    }

    return filteredDocuments?.map(({ documentList, documentCategory }) => {
      const templateId = documentList[0].externalDocumentId
      const cssId = `document-item-${templateId}`.trim()

      return (
        <React.Fragment key={templateId}>
          <Tooltip target={'#' + cssId} />
          <div
            id={cssId}
            key={templateId}
            className={classNames(styles.documentListItem)}
            data-pr-tooltip={documentCategory}
            data-pr-position="top"
          >
            <RadioButton
              inputId={templateId}
              value={templateId}
              name="category"
              onChange={e => setSelectedTemplateId(e.value)}
              checked={!!selectedTemplateId && selectedTemplateId === templateId}
            />
            <label htmlFor={templateId} className="ml-2">
              {documentCategory}
            </label>
          </div>
        </React.Fragment>
      )
    })
  }

  const footer = (
    <div
      style={{
        display: 'flex',
        justifyContent: isGeneratingDocument ? 'space-between' : 'flex-end',
        alignItems: 'center',
      }}
    >
      {isGeneratingDocument && (
        <label style={{ fontStyle: 'italic', color: 'var(--blue-500)' }}>
          Document generation is in progress. Thanks for your patience...
        </label>
      )}

      <div>
        <Button
          label="CANCEL"
          className="default-cancel-button"
          onClick={() => hide()}
          disabled={isGeneratingDocument}
        />
        <Button
          label="GENERATE DOCUMENT"
          disabled={!canGenerate}
          className="default-save-button"
          onClick={() => generateDocument()}
          loading={isGeneratingDocument}
        />
      </div>
    </div>
  )

  return (
    <Dialog
      visible={visible}
      onHide={hide}
      header="Generate Document"
      footer={footer}
      style={{ width: '100%', maxWidth: '1050px' }}
      className="default"
    >
      {isNoDocumentsFound && (
        <div className={styles.errorMessage}>
          <FontAwesomeIcon icon={faExclamationCircle} /> We did not get any Document Categories.
          Please contact SME for template revision, or open a Service Desk ticket for assistance.
        </div>
      )}

      <BlockUI
        template={blockTemplate}
        blocked={documentsQuery.isFetching || templateInfoQuery.isFetching}
        className={styles.blockUI}
      >
        <div
          className={classNames(
            styles.content,
            isGeneratingDocument && styles.generatingDocumentOverlay
          )}
        >
          <div className={styles.note}>
            Please select one document from the list provided below. If you are unable to locate the
            document you're looking for in the list below, please{' '}
            <a
              className={'text-blue-500 no-underline'}
              href={process.env.REACT_APP_EPIC_LAUNCHER_URL}
              target="_blank"
            >
              click here
            </a>{' '}
            to access the EPIC Launcher.
          </div>

          <div className={classNames(styles.searchBoxContainer, `p-input-icon-right`)}>
            <i className="pi pi-search" />
            <InputText
              placeholder="Search"
              value={searchPhrase}
              onChange={e => setSearchPhrase(e.target.value)}
              disabled={isNoDocumentsFound}
            />
          </div>

          {!documentsQuery.isFetching && (
            <div className={styles.documentListContainer}>{renderDocumentList()}</div>
          )}

          {selectedTemplateId && !templateInfoQuery.isFetching && (
            <div className={styles.settings} style={{ display: 'flex', gap: '2rem' }}>
              <div style={{ display: 'flex', alignItems: 'center', flexGrow: '1' }}>
                <label>File name</label>
                <InputText
                  style={{ flexGrow: '1' }}
                  value={documentName}
                  onChange={e => setDocumentName(e.target.value)}
                />
              </div>

              <div style={{ display: 'flex', alignItems: 'center' }}>
                <label>Document type</label>
                <Dropdown
                  style={{ minWidth: '12rem' }}
                  options={documentTypeOptions}
                  value={selectedDocumentType}
                  optionLabel="label"
                  optionValue="value"
                  onChange={e => setSelectedDocumentType(e.value)}
                />
              </div>
            </div>
          )}
        </div>
      </BlockUI>
    </Dialog>
  )
}

export default GenerateDocumentModal
